import { useState, useEffect } from "react";
import { getCategories, addCategory, updateCategory, deleteCategory } from "../../utils/firebase";

const EMOJI_OPTIONS = ["📱","🍕","🎯","✈️","🎮","🎬","🎪","💪","📚","🎨","🏆","🌟"];

function EmojiRow({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
      {EMOJI_OPTIONS.map(em => (
        <button key={em} type="button" onClick={() => onChange(em)}
          style={{
            background: value === em ? "var(--blue)" : "var(--surface2)",
            border: `2px solid ${value === em ? "var(--blue)" : "var(--border)"}`,
            borderRadius: 8, width: 36, height: 36, cursor: "pointer",
            fontSize: "1.1rem", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >{em}</button>
      ))}
    </div>
  );
}

export function CategoryManager({ data }) {
  const [categories, setCategories] = useState([]);
  const [showAdd, setShowAdd]       = useState(false);
  const [formName, setFormName]     = useState("");
  const [formEmoji, setFormEmoji]   = useState("📱");
  const [editingId, setEditingId]   = useState(null);
  const [editName, setEditName]     = useState("");
  const [editEmoji, setEditEmoji]   = useState("📱");
  const [error, setError]           = useState("");

  useEffect(() => { getCategories().then(setCategories); }, []);

  function rewardCount(catId) {
    return (data.rewards || []).filter(r => r.categoryId === catId).length;
  }

  async function handleAdd() {
    if (!formName.trim()) return;
    const slug = formName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    const id = `${slug}_${Date.now()}`;
    await addCategory({ id, name: formName.trim(), emoji: formEmoji });
    setCategories(await getCategories());
    setFormName(""); setFormEmoji("📱"); setShowAdd(false);
  }

  function startEdit(cat) {
    setEditingId(cat.id); setEditName(cat.name); setEditEmoji(cat.emoji);
    setShowAdd(false);
  }

  async function handleSaveEdit(id) {
    if (!editName.trim()) return;
    await updateCategory(id, { name: editName.trim(), emoji: editEmoji });
    setCategories(await getCategories());
    setEditingId(null);
  }

  async function handleDelete(id) {
    try {
      await deleteCategory(id);
      setCategories(await getCategories());
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(""), 3500);
    }
  }

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <div className="card-title">Reward Categories</div>
        <button className="wm-btn"
          onClick={() => { setShowAdd(s => !s); setEditingId(null); setFormName(""); setFormEmoji("📱"); }}>
          {showAdd ? "Cancel" : "+ Add Category"}
        </button>
      </div>

      {error && (
        <div style={{ background: "var(--red-light)", color: "var(--red)", borderRadius: 9, padding: ".5rem .75rem", fontSize: ".82rem", fontWeight: 700, marginBottom: ".75rem" }}>
          {error}
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div style={{ background: "var(--surface2)", borderRadius: 12, padding: "1rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: ".65rem" }}>
            <input className="wm-input" placeholder="Category name (required)" value={formName} onChange={e => setFormName(e.target.value)} />
            <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--muted)" }}>Pick an emoji</div>
            <EmojiRow value={formEmoji} onChange={setFormEmoji} />
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button className="wm-btn" onClick={handleAdd} disabled={!formName.trim()}>Save</button>
              <button className="wm-btn secondary" onClick={() => { setShowAdd(false); setFormName(""); setFormEmoji("📱"); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* 2-column grid */}
      {categories.length === 0 ? (
        <div className="empty-state">No categories yet — add one above!</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {categories.map(cat => {
            const count     = rewardCount(cat.id);
            const isEditing = editingId === cat.id;
            return (
              <div key={cat.id} style={{
                gridColumn: isEditing ? "span 2" : "span 1",
                borderRadius: 14, border: "1.5px solid var(--border)",
                background: "var(--surface)", display: "flex", flexDirection: "column", overflow: "hidden",
              }}>
                {!isEditing ? (
                  <>
                    <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--surface2)", fontSize: "2rem", flexShrink: 0 }}>
                      {cat.emoji}
                    </div>
                    <div style={{ padding: "8px 10px 6px", flex: 1, display: "flex", flexDirection: "column", gap: ".2rem" }}>
                      <div style={{ fontWeight: 800, fontSize: ".85rem" }}>{cat.name}</div>
                      <div style={{ fontSize: ".72rem", color: "var(--muted)" }}>{count} reward{count !== 1 ? "s" : ""}</div>
                    </div>
                    <div style={{ display: "flex", gap: ".35rem", padding: "0 8px 8px" }}>
                      <button className="wm-btn secondary" style={{ flex: 1, padding: ".3rem", fontSize: ".75rem" }}
                        onClick={() => startEdit(cat)}>✏️</button>
                      <button className="wm-btn danger" style={{ flex: 1, padding: ".3rem", fontSize: ".75rem" }}
                        disabled={count > 0}
                        title={count > 0 ? "Remove rewards from this category first" : "Delete category"}
                        onClick={() => handleDelete(cat.id)}>✕</button>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: "1rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".65rem" }}>
                      <div style={{ fontWeight: 700, fontSize: ".88rem" }}>Edit Category</div>
                      <button className="wm-btn secondary" style={{ padding: ".3rem .65rem", fontSize: ".78rem" }} onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".65rem" }}>
                      <input className="wm-input" placeholder="Category name" value={editName} onChange={e => setEditName(e.target.value)} />
                      <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--muted)" }}>Pick an emoji</div>
                      <EmojiRow value={editEmoji} onChange={setEditEmoji} />
                      <div style={{ display: "flex", gap: ".5rem" }}>
                        <button className="wm-btn" onClick={() => handleSaveEdit(cat.id)} disabled={!editName.trim()}>Save</button>
                        <button className="wm-btn secondary" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
